import { apiClient } from '../utils/apiClient';

export interface HistoryUser {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    profilePicture?: string;
}

export interface ChangeSummary {
    chaptersAdded?: number;
    chaptersRemoved?: number;
    blocksAdded?: number;
    blocksRemoved?: number;
    fieldsChanged?: string[];
    restoredFromVersion?: number;
}

export interface HistoryEntry {
    _id: string;
    dossierId: string;
    action: 'created' | 'content_updated' | 'settings_updated' | 'author_added' | 'author_removed' | 'author_changed' | 'restored' | 'deleted';
    userId: HistoryUser;
    versionNumber: number;
    changeSummary: ChangeSummary;
    snapshotSizeBytes: number;
    createdAt: string;
}

export interface HistoryResponse {
    success: boolean;
    entries: HistoryEntry[];
    total: number;
    pages: number;
}

export interface VersionResponse {
    success: boolean;
    version: HistoryEntry & {
        snapshot: any;
    };
}

export interface RestoreResponse {
    success: boolean;
    message: string;
    dossier: any;
}

/**
 * Récupère l'historique paginé d'un dossier
 */
export const getDossierHistory = async (
    dossierId: string,
    page: number = 1,
    limit: number = 20
): Promise<HistoryResponse | null> => {
    try {
        const response = await apiClient(
            `/dossiers/${dossierId}/history?page=${page}&limit=${limit}`
        );

        if (!response.ok) {
            console.error('Erreur récupération historique:', response.status);
            return null;
        }

        return await response.json();
    } catch (error) {
        console.error('Erreur récupération historique:', error);
        return null;
    }
};

/**
 * Récupère une version spécifique avec son snapshot
 */
export const getDossierVersion = async (
    dossierId: string,
    versionNumber: number
): Promise<VersionResponse | null> => {
    try {
        const response = await apiClient(
            `/dossiers/${dossierId}/history/${versionNumber}`
        );

        if (!response.ok) {
            console.error('Erreur récupération version:', response.status);
            return null;
        }

        return await response.json();
    } catch (error) {
        console.error('Erreur récupération version:', error);
        return null;
    }
};

/**
 * Restaure un dossier à une version précédente
 */
export const restoreDossierVersion = async (
    dossierId: string,
    versionNumber: number
): Promise<RestoreResponse | null> => {
    try {
        const response = await apiClient(
            `/dossiers/${dossierId}/history/${versionNumber}/restore`,
            { method: 'POST' }
        );

        if (!response.ok) {
            const errorData = await response.json();
            console.error('Erreur restauration:', errorData.message);
            return null;
        }

        return await response.json();
    } catch (error) {
        console.error('Erreur restauration:', error);
        return null;
    }
};

/**
 * Formate une action en libellé français
 */
export const formatActionLabel = (action: HistoryEntry['action']): string => {
    const labels: Record<HistoryEntry['action'], string> = {
        created: 'Création',
        content_updated: 'Modification',
        settings_updated: 'Nouveaux paramètres',
        author_added: 'Co-auteur ajouté',
        author_removed: 'Co-auteur retiré',
        author_changed: 'Auteur modifié',
        restored: 'Restauration',
        deleted: 'Suppression'
    };
    return labels[action] || action;
};

/**
 * Formate le résumé des changements
 */
export const formatChangeSummary = (summary: ChangeSummary): string[] => {
    const parts: string[] = [];

    if (summary.restoredFromVersion) {
        parts.push(`Restauré depuis la version ${summary.restoredFromVersion}`);
    }

    if (summary.blocksAdded && summary.blocksAdded > 0) {
        parts.push(`+${summary.blocksAdded} bloc${summary.blocksAdded > 1 ? 's' : ''}`);
    }

    if (summary.blocksRemoved && summary.blocksRemoved > 0) {
        parts.push(`-${summary.blocksRemoved} bloc${summary.blocksRemoved > 1 ? 's' : ''}`);
    }

    if (summary.chaptersAdded && summary.chaptersAdded > 0) {
        parts.push(`+${summary.chaptersAdded} chapitre${summary.chaptersAdded > 1 ? 's' : ''}`);
    }

    if (summary.chaptersRemoved && summary.chaptersRemoved > 0) {
        parts.push(`-${summary.chaptersRemoved} chapitre${summary.chaptersRemoved > 1 ? 's' : ''}`);
    }

    if (summary.fieldsChanged && summary.fieldsChanged.length > 0) {
        const fieldLabels: Record<string, string> = {
            title: 'Titre',
            description: 'Description',
            'settings.visibility': 'Visibilité',
            'seo.seoTitle': 'Titre SEO',
            'seo.seoDescription': 'Description SEO',
            'seo.seoKeyword': 'Mots-clés SEO',
            'seo.seoThumbnail': 'Miniature SEO'
        };

        const changedLabels = summary.fieldsChanged.map(f => fieldLabels[f] || f);
        parts.push(`${changedLabels.join(', ')} modifié${changedLabels.length > 1 ? 's' : ''}`);
    }

    return parts;
};

/**
 * Formate une date en format relatif ("Il y a 2 heures")
 */
export const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
        return 'À l\'instant';
    } else if (diffMinutes < 60) {
        return `Il y a ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
    } else if (diffHours < 24) {
        return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
    } else if (diffDays === 1) {
        return 'Hier';
    } else if (diffDays < 7) {
        return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    } else {
        return formatAbsoluteDate(dateString);
    }
};

/**
 * Formate une date en format absolu ("15 janv. 2026 à 14:32")
 */
export const formatAbsoluteDate = (dateString: string): string => {
    const date = new Date(dateString);
    const months = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day} ${month} ${year} à ${hours}:${minutes}`;
};
